import { ChangeEvent, Dispatch, FC, SetStateAction, useEffect, useRef, useState } from 'react';
import { TextField, Grid, Button, makeStyles, Typography } from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import { useApi } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import type { RancherProject } from '@runway/devkit-common';
import { CreateRancherNamespaceDialog } from './CreateRancherNamespaceDialog';
import { Squad360ServiceApiRef, ClusterServiceApiRef } from '../../services';

type NamespaceSelectorProps = {
  selectedCluster: string | null;
  setSelectedCluster?: Dispatch<SetStateAction<string | null>>;
  isCreateButtonVisible: boolean;
  onChange(namespace: string): void;
  selectedNamespaceName?: string;
};
type ProjectAndNamespace = {
  name: string;
  namespace: string;
};

const useStyles = makeStyles(theme => ({
  formGroup: {
    margin: theme.spacing(2),
    minWidth: 195,
  },
}));

export const NamespaceSelector: FC<NamespaceSelectorProps> = ({
  selectedCluster,
  setSelectedCluster,
  selectedNamespaceName = '',
  isCreateButtonVisible,
  onChange,
}) => {
  const squad360 = useApi(Squad360ServiceApiRef);
  const clusterService = useApi(ClusterServiceApiRef);
  const [availableNamespaces, setAvailableNamespaces] = useState<string[]>([]);
  const classes = useStyles();
  const [createRancherNamespaceDialogOpen, setCreateRancherNamespaceDialogOpen] = useState(false);
  const [isSquadMember, setIsSquadMember] = useState(false);
  const [selectedNamespace, setSelectedNamespace] = useState<string>(selectedNamespaceName);
  const previousClusterRef = useRef<string | null>(selectedCluster);

  const filterNamespaces = async (projectsAndNamespaces: ProjectAndNamespace[]) => {
    const ownSupportedApps = await squad360.getOwnSupportedApps();
    if (ownSupportedApps.length > 0) {
      setIsSquadMember(true);
    }
    const appShortNames: string[] = ownSupportedApps
      .map((archerApp: { shortName?: string }) => archerApp.shortName)
      .filter((shortName): shortName is string => Boolean(shortName));
    const returnedNamespaces = new Set<string>();
    for (const object of projectsAndNamespaces) {
      for (const appShortName of appShortNames) {
        if (object.namespace.includes(appShortName.toLowerCase()) || object.name.includes(appShortName.toLowerCase())) {
          returnedNamespaces.add(object.namespace);
        }
      }
    }
    return [...returnedNamespaces];
  };

  const getNamespaces = useAsync(async () => {
    if (!selectedCluster) {
      return;
    }
    try {
      const projects = await clusterService.getProjectsByCluster(selectedCluster);
      const projectsAndNamespaces: ProjectAndNamespace[] = [];
      projects.forEach((item: RancherProject) => {
        projectsAndNamespaces.push(item);
      });
      if (projectsAndNamespaces.length > 0) {
        const filteredNamespacesResp = await filterNamespaces(projectsAndNamespaces);
        setAvailableNamespaces(filteredNamespacesResp);
      }
    } catch (error: unknown) {
      throw new Error('Cannot retrieve namespaces', { cause: error });
    }
  }, [selectedCluster, createRancherNamespaceDialogOpen]);

  useEffect(() => {
    if (previousClusterRef.current === selectedCluster) {
      return;
    }
    previousClusterRef.current = selectedCluster;
    // Clear the previously selected namespace and its stale option list so that a namespace
    // belonging to the prior cluster can never remain selected against the newly chosen cluster.
    setSelectedNamespace('');
    setAvailableNamespaces([]);
    onChange('');
  }, [selectedCluster, onChange]);

  useEffect(() => {
    if (selectedNamespace && !availableNamespaces.includes(selectedNamespace)) {
      setAvailableNamespaces([selectedNamespace, ...availableNamespaces]);
      onChange(selectedNamespace);
    }
    // will cause infite renders if all deps are in the array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNamespace]);

  const hasSelectedCluster = selectedCluster !== null && selectedCluster !== '';

  return (
    <Grid>
      <Grid item xs={12} md={12}>
        {!selectedCluster ? (
          <Alert severity="warning">Select a cluster to get available namespaces</Alert>
        ) : (
          !getNamespaces.loading &&
          !getNamespaces.error &&
          !isSquadMember && (
            <Alert style={{ marginTop: 10 }} severity="error">
              <strong>
                There was an issue getting the Squad Info.{' '}
                <a href="https://squad360.aa.com/" target="_blank">
                  Click <u>Squad360 to assign a squad to your AAID</u>
                </a>
              </strong>
            </Alert>
          )
        )}
        {availableNamespaces.length === 0 &&
          isSquadMember &&
          hasSelectedCluster &&
          !getNamespaces.loading &&
          !getNamespaces.error && (
            <Alert severity="warning">
              No available namespaces to select from. Select another cluster or create a namespace through our
              Infrastructure plugin.
            </Alert>
          )}
        {isSquadMember &&
          !getNamespaces.loading &&
          hasSelectedCluster &&
          (availableNamespaces.length > 0 || selectedNamespace) && (
            <Autocomplete
              id="namespace"
              data-testid="namespace_selector"
              options={availableNamespaces}
              disabled={getNamespaces.loading || availableNamespaces.length === 0}
              value={selectedNamespace}
              onChange={(_event: ChangeEvent<object>, namespace: string | null) => {
                setSelectedNamespace(namespace ?? '');
                onChange(namespace ?? '');
              }}
              onInputChange={(_event: ChangeEvent<object>, namespace: string) => {
                onChange(namespace);
              }}
              getOptionLabel={option => (availableNamespaces.length === 0 ? '' : option)}
              renderInput={params => <TextField required {...params} label="Namespace" variant="outlined" />}
            />
          )}
        {getNamespaces.loading && <Alert severity="info">Loading Namespaces</Alert>}
        {getNamespaces.error && <Alert severity="error">Cannot get namespaces for cluster {selectedCluster}</Alert>}
      </Grid>
      {isCreateButtonVisible && (
        <Grid item xs={6} md={6}>
          <div className={classes.formGroup}>
            <Button
              data-testid="Rancher-Namespace-Creation"
              variant="contained"
              color="primary"
              onClick={() => {
                setCreateRancherNamespaceDialogOpen(true);
              }}
            >
              Create Rancher Namespace
            </Button>
            <Typography variant="body2" style={{ width: 300, fontSize: '0.8em', display: 'block' }}>
              <strong>Create Rancher Project/Namespace if don't have one already.</strong>
            </Typography>
            <CreateRancherNamespaceDialog
              isOpen={createRancherNamespaceDialogOpen}
              close={() => setCreateRancherNamespaceDialogOpen(false)}
              setNamespace={setSelectedNamespace}
              setSelectedCluster={setSelectedCluster}
              onChange={onChange}
              setAvailableNamespaces={setAvailableNamespaces}
              availableNamespaces={availableNamespaces}
            />
          </div>
        </Grid>
      )}
    </Grid>
  );
};
